/**
 * MapLibreEngine — V2 map container.
 *
 * Dual-mode: Dark branded at low zoom -> Light detailed at high zoom.
 * Uses a single light base style (positron) with a dark overlay.
 * Overlay fades with zoom: dark premium at overview -> light readable at detail.
 * No style switching = zero layer loss, zero flicker.
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { ZoomIn, ZoomOut, Maximize2, Sun, Moon } from 'lucide-react';
import { ZOOM, SAUDI, CAMERA } from './interactions/constants';
import RegionPolygonsLayer from './layers/RegionPolygonsLayer';
import ProjectClusterLayer from './layers/ProjectClusterLayer';
import '../../styles/map-v2-theme.css';
import '../../styles/map-theme.css';

/* ── Load RTL text plugin once ── */
try {
  maplibregl.setRTLTextPlugin(
    'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js',
    true
  );
} catch (_) { /* already loaded */ }

/* ── Single base style — dark look comes from overlay ── */
const BASE_STYLE = 'https://tiles.openfreemap.org/styles/positron';

/* ── Zoom thresholds for dark/light visual transition ── */
const THEME_TRANSITION_START = 8;
const THEME_TRANSITION_END = 11;
const THEME_CSS_THRESHOLD = 9;

/* ── URL override: ?theme=light forces light mode ── */
function getUrlTheme() {
  try {
    const t = new URLSearchParams(window.location.search).get('theme');
    if (t === 'light') return 'light';
  } catch (_) {}
  return null;
}

/* ── Dynamic padding based on viewport ── */
function calcFitPadding() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (w < 768) return { top: 50, bottom: 70, left: 30, right: 30 };
  return {
    top: Math.round(h * 0.06),
    bottom: Math.round(h * 0.12),
    left: Math.round(w * 0.04),
    right: Math.round(w * 0.06),
  };
}

/* ── World polygon for dark overlay ── */
const WORLD_GEOJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]]],
    },
  }],
};

/* ── Our custom layer IDs (skip in label processing) ── */
const OWN_LAYERS = new Set([
  'v2-dark-overlay',
  'saudi-mask-fill', 'saudi-interior-fill',
  'starfield-bg', 'saudi-radial-glow',
  'saudi-depth-shadow',
  'saudi-border-glow-outer', 'saudi-border-glow-inner',
  'regions-fill', 'regions-dim', 'regions-border',
  'regions-hover-border', 'regions-hover-fill',
  'region-labels-text',
  'project-clusters', 'project-clusters-hitarea', 'project-clusters-text',
  'project-points', 'project-points-center', 'project-points-hitarea',
  'project-points-glow', 'project-points-pulse', 'project-category-label',
  'project-featured-dot', 'project-points-hover',
]);

/* ── Label visibility config (which base-style labels to show and at what zoom) ── */
const LABEL_CONFIG = {
  label_country_1: null, label_country_2: null, label_country_3: null, label_state: null,
  label_city_capital: 9, label_city: 9,
  label_town: 10, label_village: 11, label_other: 11,
  'highway-name-major': 11, 'highway-name-minor': 13, 'highway-name-path': 14,
  'highway-shield-non-us': 12,
  'highway-shield-us-interstate': null, road_shield_us: null,
  road_one_way_arrow: 15, road_one_way_arrow_opposite: 15,
  waterway_line_label: 11, water_name_point_label: 10, water_name_line_label: 10,
  airport: 11, poi_transit: 12, poi_r1: 14, poi_r7: 15, poi_r20: 16,
};

/* ── Configure base-style labels for current mode ── */
function configureLabels(map, mode) {
  const style = map.getStyle();
  if (!style?.layers) return;
  const labelsToMove = [];

  style.layers.forEach(layer => {
    if (layer.type !== 'symbol') return;
    if (OWN_LAYERS.has(layer.id)) return;

    if (mode === 'light') {
      const config = LABEL_CONFIG[layer.id];
      if (config === null) return; // Keep country labels hidden
      try {
        map.setLayoutProperty(layer.id, 'visibility', 'visible');
        map.setPaintProperty(layer.id, 'text-color', '#333');
        map.setPaintProperty(layer.id, 'text-halo-color', 'rgba(255,255,255,0.8)');
        map.setPaintProperty(layer.id, 'text-halo-width', 1.2);
      } catch (_) {}
      return;
    }

    // Auto or dark mode
    const config = LABEL_CONFIG[layer.id];
    if (config === null) {
      try { map.setLayoutProperty(layer.id, 'visibility', 'none'); } catch (_) {}
      return;
    }
    if (config !== undefined) {
      try {
        map.setLayoutProperty(layer.id, 'visibility', 'visible');
        map.setLayerZoomRange(layer.id, config, layer.maxzoom || 24);
        if (mode === 'auto') {
          map.setPaintProperty(layer.id, 'text-color',
            ['interpolate', ['linear'], ['zoom'],
              4, '#e8e4dc', THEME_TRANSITION_START, '#e8e4dc',
              THEME_TRANSITION_END, '#333']);
          map.setPaintProperty(layer.id, 'text-halo-color',
            ['interpolate', ['linear'], ['zoom'],
              4, 'rgba(4,10,20,0.6)', THEME_TRANSITION_START, 'rgba(4,10,20,0.6)',
              THEME_TRANSITION_END, 'rgba(255,255,255,0.8)']);
        } else {
          map.setPaintProperty(layer.id, 'text-color', '#e8e4dc');
          map.setPaintProperty(layer.id, 'text-halo-color', 'rgba(4,10,20,0.6)');
        }
        map.setPaintProperty(layer.id, 'text-halo-width', 1);
      } catch (_) {}
      labelsToMove.push(layer.id);
    } else {
      try { map.setLayoutProperty(layer.id, 'visibility', 'none'); } catch (_) {}
    }
  });

  /* Move configured labels above custom fill layers for readability */
  if (mode !== 'light' && labelsToMove.length) {
    setTimeout(() => {
      labelsToMove.forEach(id => {
        try { map.moveLayer(id); } catch (_) {}
      });
    }, 300);
    setTimeout(() => {
      labelsToMove.forEach(id => {
        try { map.moveLayer(id); } catch (_) {}
      });
    }, 1500);
  }
}

/* ── Apply theme to overlay, mask, interior, regions, region-labels ── */
function applyThemeOverlay(map, mode) {
  /* Dark overlay */
  if (map.getLayer('v2-dark-overlay')) {
    if (mode === 'light') {
      map.setPaintProperty('v2-dark-overlay', 'fill-opacity', 0);
    } else {
      map.setPaintProperty('v2-dark-overlay', 'fill-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.82, 7, 0.6, 9, 0.35, 11, 0.15, 13, 0]);
    }
  }

  /* Saudi mask (outside borders) */
  if (map.getLayer('saudi-mask-fill')) {
    if (mode === 'light') {
      map.setPaintProperty('saudi-mask-fill', 'fill-color', '#EDE8DD');
    } else if (mode === 'auto') {
      map.setPaintProperty('saudi-mask-fill', 'fill-color',
        ['interpolate', ['linear'], ['zoom'],
          4, '#040a14', THEME_TRANSITION_START, '#040a14',
          THEME_TRANSITION_END, '#EDE8DD']);
    } else {
      map.setPaintProperty('saudi-mask-fill', 'fill-color', '#040a14');
    }
  }

  /* Saudi interior (gap filler) — fades at high zoom to reveal basemap details */
  if (map.getLayer('saudi-interior-fill')) {
    if (mode === 'light') {
      map.setPaintProperty('saudi-interior-fill', 'fill-color', '#d5cfc3');
      map.setPaintProperty('saudi-interior-fill', 'fill-opacity', 0);
    } else if (mode === 'auto') {
      map.setPaintProperty('saudi-interior-fill', 'fill-color',
        ['interpolate', ['linear'], ['zoom'],
          4, '#0a1520', THEME_TRANSITION_START, '#0a1520',
          THEME_TRANSITION_END, '#d5cfc3']);
      map.setPaintProperty('saudi-interior-fill', 'fill-opacity',
        ['interpolate', ['linear'], ['zoom'],
          4, 1, ZOOM.CITY_MAX, 0.9, 11, 0.3, ZOOM.PROJECT_MIN, 0]);
    } else {
      map.setPaintProperty('saudi-interior-fill', 'fill-color', '#0a1520');
      map.setPaintProperty('saudi-interior-fill', 'fill-opacity', 1);
    }
  }

  /* Region fills — fade earlier to reveal basemap road/building details */
  if (map.getLayer('regions-fill')) {
    if (mode === 'light') {
      map.setPaintProperty('regions-fill', 'fill-opacity',
        ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 0.45, ZOOM.CITY_MAX, 0.15, 11, 0.05, 12, 0]);
    } else {
      map.setPaintProperty('regions-fill', 'fill-opacity',
        ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 0.94, ZOOM.CITY_MAX, 0.5, 11, 0.15, ZOOM.PROJECT_MIN, 0]);
    }
  }

  /* Region dim (hover dimming) */
  if (map.getLayer('regions-dim')) {
    if (mode === 'light') {
      map.setPaintProperty('regions-dim', 'fill-color', 'rgba(237, 232, 221, 0.5)');
    } else {
      map.setPaintProperty('regions-dim', 'fill-color', 'rgba(2, 5, 12, 0.55)');
    }
  }

  /* Region borders */
  if (map.getLayer('regions-border')) {
    if (mode === 'light') {
      map.setPaintProperty('regions-border', 'line-color', 'rgba(80, 70, 55, 0.25)');
      map.setPaintProperty('regions-border', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 0.3, ZOOM.DENSE_MAX, 0.15, ZOOM.PROJECT_MIN, 0]);
    } else if (mode === 'auto') {
      map.setPaintProperty('regions-border', 'line-color',
        ['interpolate', ['linear'], ['zoom'],
          4, 'rgba(245, 241, 232, 0.22)', THEME_TRANSITION_END, 'rgba(80, 70, 55, 0.25)']);
      map.setPaintProperty('regions-border', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 1, ZOOM.DENSE_MAX, 0.5, ZOOM.PROJECT_MIN, 0]);
    } else {
      map.setPaintProperty('regions-border', 'line-color', 'rgba(245, 241, 232, 0.22)');
      map.setPaintProperty('regions-border', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 1, ZOOM.DENSE_MAX, 0.5, ZOOM.PROJECT_MIN, 0]);
    }
  }

  /* Region labels */
  if (map.getLayer('region-labels-text')) {
    if (mode === 'light') {
      map.setPaintProperty('region-labels-text', 'text-color', '#2D3A2E');
      map.setPaintProperty('region-labels-text', 'text-halo-color', 'rgba(237,232,221,0.7)');
    } else if (mode === 'auto') {
      map.setPaintProperty('region-labels-text', 'text-color',
        ['interpolate', ['linear'], ['zoom'],
          4, '#FFFFFF', THEME_TRANSITION_START, '#FFFFFF', THEME_TRANSITION_END, '#2D3A2E']);
      map.setPaintProperty('region-labels-text', 'text-halo-color',
        ['interpolate', ['linear'], ['zoom'],
          4, 'rgba(0, 0, 0, 0.5)', THEME_TRANSITION_START, 'rgba(0, 0, 0, 0.5)',
          THEME_TRANSITION_END, 'rgba(237,232,221,0.7)']);
    } else {
      map.setPaintProperty('region-labels-text', 'text-color', '#FFFFFF');
      map.setPaintProperty('region-labels-text', 'text-halo-color', 'rgba(0, 0, 0, 0.5)');
    }
  }

  /* ── Visual enhancements: adapt to theme ── */

  /* Starfield */
  if (map.getLayer('starfield-bg')) {
    map.setPaintProperty('starfield-bg', 'fill-opacity',
      mode === 'light' ? 0 : ['interpolate', ['linear'], ['zoom'], 4, 1.0, 9, 0.55, 13, 0.1]);
  }

  /* Network lines — removed: replaced by constellation pattern in starfield */

  /* Radial glow */
  if (map.getLayer('saudi-radial-glow')) {
    map.setPaintProperty('saudi-radial-glow', 'circle-opacity',
      mode === 'light' ? 0 : ['interpolate', ['linear'], ['zoom'], 4, 0.55, 9, 0.2, 13, 0.03]);
  }

  /* Border glow */
  if (map.getLayer('saudi-border-glow-outer')) {
    if (mode === 'light') {
      map.setPaintProperty('saudi-border-glow-outer', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.08, 9, 0.05, 13, 0.02]);
    } else {
      map.setPaintProperty('saudi-border-glow-outer', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.54, 9, 0.34, 13, 0.10]);
    }
  }
  if (map.getLayer('saudi-border-glow-inner')) {
    if (mode === 'light') {
      map.setPaintProperty('saudi-border-glow-inner', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.15, 9, 0.1, 13, 0.03]);
    } else {
      map.setPaintProperty('saudi-border-glow-inner', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.90, 9, 0.60, 13, 0.22]);
    }
  }

  /* Depth shadow */
  if (map.getLayer('saudi-depth-shadow')) {
    if (mode === 'light') {
      map.setPaintProperty('saudi-depth-shadow', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.1, 9, 0.06, 13, 0.02]);
    } else {
      map.setPaintProperty('saudi-depth-shadow', 'line-opacity',
        ['interpolate', ['linear'], ['zoom'], 4, 0.35, 9, 0.2, 13, 0.06]);
    }
  }
}

const MapLibreEngine = ({
  filters = {},
  layers = {},
  onResultCount,
  onProjectSelect,
  resetRef,
  zoomToRegionRef,
  zoomToCoordRef,
  presentationMode = false,
  statsMode = false,
  onStatsRegionSelect,
  filterOpen = false,
  projectOpen = false,
  children,
}) => {
  const outerRef = useRef(null);
  const canvasRef = useRef(null);
  const mapRef = useRef(null);
  const shootingRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [isLight, setIsLight] = useState(false);
  const [manualTheme, setManualTheme] = useState(() => getUrlTheme());
  const currentModeRef = useRef(getUrlTheme() || 'auto');

  /* ── Initialize MapLibre ── */
  useEffect(() => {
    if (!canvasRef.current || mapRef.current) return;
    document.documentElement.classList.add('namaa-map-active');

    const initMode = currentModeRef.current;

    const map = new maplibregl.Map({
      container: canvasRef.current,
      style: BASE_STYLE,
      center: SAUDI.CENTER,
      zoom: ZOOM.INITIAL,
      minZoom: ZOOM.MIN,
      maxZoom: ZOOM.MAX,
      attributionControl: false,
      fadeDuration: 200,
    });

    map.on('load', () => {
      /* 1. Add dark overlay — START FULLY OPAQUE to hide base tiles */
      if (!map.getSource('v2-dark-overlay')) {
        map.addSource('v2-dark-overlay', { type: 'geojson', data: WORLD_GEOJSON });
        map.addLayer({
          id: 'v2-dark-overlay',
          type: 'fill',
          source: 'v2-dark-overlay',
          paint: {
            'fill-color': '#040a14',
            'fill-opacity': initMode === 'light' ? 0 : 1,
            'fill-opacity-transition': { duration: 800, delay: 0 },
          },
        });
      }

      /* 2. Configure labels for initial mode */
      configureLabels(map, initMode === 'light' ? 'light' : 'auto');

      /* 3. Signal ready — RegionPolygonsLayer will mount and add layers */
      mapRef.current = map;
      window.__v2map = map;
      setMapReady(true);
      setIsLight(initMode === 'light');

      /* 4. fitBounds */
      map.fitBounds(SAUDI.BOUNDS, {
        padding: calcFitPadding(),
        duration: 0,
        maxZoom: ZOOM.OVERVIEW,
      });

      /* 5. DELAYED reveal — wait for RegionPolygonsLayer to add all layers,
            then fade in canvas and transition overlay to zoom-interpolated opacity */
      setTimeout(() => {
        if (canvasRef.current) canvasRef.current.classList.add('v2-canvas-ready');
        /* After canvas starts appearing, transition overlay to normal zoom-based opacity */
        setTimeout(() => {
          if (initMode !== 'light') {
            try {
              map.setPaintProperty('v2-dark-overlay', 'fill-opacity',
                ['interpolate', ['linear'], ['zoom'], 4, 0.82, 7, 0.6, 9, 0.35, 11, 0.15, 13, 0]);
            } catch (_) {}
          }
        }, 400);
      }, 700);

      /* 5. Debounced resize handler */
      let resizeTimer = null;
      const handleResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          map.resize();
          map.fitBounds(SAUDI.BOUNDS, {
            padding: calcFitPadding(),
            duration: 300,
            maxZoom: ZOOM.OVERVIEW,
          });
        }, 150);
      };
      window.addEventListener('resize', handleResize);
      map._namaaResizeHandler = handleResize;

      /* 6. Theme is fixed — only changes via manual toggle button, NEVER by zoom */

      /* 7. If starting in light mode from URL, apply overlay immediately */
      if (initMode === 'light') {
        applyThemeOverlay(map, 'light');
      }
    });

    return () => {
      document.documentElement.classList.remove('namaa-map-active');
      if (map._namaaResizeHandler) {
        window.removeEventListener('resize', map._namaaResizeHandler);
      }
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  /* ── React to manual theme changes ── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const mode = manualTheme || 'dark';
    currentModeRef.current = mode;

    applyThemeOverlay(map, mode === 'dark' ? 'auto' : 'light');
    configureLabels(map, mode === 'dark' ? 'auto' : 'light');

    setIsLight(mode === 'light');
  }, [manualTheme, mapReady]);

  /* ── Toggle theme: dark <-> light (manual only, never auto) ── */
  const handleThemeToggle = useCallback(() => {
    setManualTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  /* ── Zoom helpers ── */
  const handleReset = useCallback(() => {
    mapRef.current?.fitBounds(SAUDI.BOUNDS, {
      padding: calcFitPadding(),
      duration: CAMERA.FLY_DURATION,
      easing: CAMERA.EASE_OUT_CUBIC,
      maxZoom: ZOOM.OVERVIEW,
    });
    if (onProjectSelect) onProjectSelect(null);
  }, [onProjectSelect]);

  useEffect(() => { if (resetRef) resetRef.current = handleReset; }, [resetRef, handleReset]);

  const zoomToRegionById = useCallback((geoId) => {
    if (!mapRef.current) return;
    try {
      const saudiData = require('../../data/saudi_regions_simplified.json');
      const feature = saudiData.features.find(f => f.properties.id === geoId);
      if (!feature) { handleReset(); return; }
      const coords = feature.geometry.type === 'MultiPolygon'
        ? feature.geometry.coordinates.flat(2)
        : feature.geometry.coordinates.flat(1);
      let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
      coords.forEach(([lng, lat]) => {
        if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
      });
      mapRef.current.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: CAMERA.REGION_PADDING, duration: CAMERA.REGION_FLY_DURATION, maxZoom: 8,
          easing: CAMERA.EASE_IN_OUT_QUART, essential: true }
      );
    } catch (_) { handleReset(); }
  }, [handleReset]);

  useEffect(() => { if (zoomToRegionRef) zoomToRegionRef.current = zoomToRegionById; }, [zoomToRegionRef, zoomToRegionById]);

  const zoomToCoord = useCallback((lng, lat, targetZoom = 10) => {
    const duration = targetZoom >= 13 ? 2200 : CAMERA.CLUSTER_EASE_DURATION;
    mapRef.current?.flyTo({
      center: [lng, lat], zoom: targetZoom,
      duration: duration,
      easing: CAMERA.EASE_IN_OUT_QUART, essential: true,
    });
  }, []);

  useEffect(() => { if (zoomToCoordRef) zoomToCoordRef.current = zoomToCoord; }, [zoomToCoordRef, zoomToCoord]);

  /* ── Shooting star animation ── */
  useEffect(() => {
    const cvs = shootingRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    let animId;
    const mouse = { x: -9999, y: -9999 };
    const GLOW_RADIUS = 180;

    // Stars: fixed positions, each twinkles independently
    let stars = [];
    const resize = () => {
      cvs.width = cvs.offsetWidth;
      cvs.height = cvs.offsetHeight;
      // Generate stars on resize
      const count = Math.floor((cvs.width * cvs.height) / 6000);
      stars = [];
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * cvs.width,
          y: Math.random() * cvs.height,
          r: 0.5 + Math.random() * 1.8,
          phase: Math.random() * Math.PI * 2,
          speed: 0.015 + Math.random() * 0.04,
          baseAlpha: 0.25 + Math.random() * 0.6,
        });
      }
    };
    resize();
    window.addEventListener('resize', resize);

    // Track mouse for glow effect
    const onMove = (e) => {
      const rect = cvs.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };
    cvs.parentElement.addEventListener('mousemove', onMove);
    cvs.parentElement.addEventListener('mouseleave', onLeave);

    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      t++;

      for (const s of stars) {
        // Twinkle
        const twinkle = Math.sin(t * s.speed + s.phase);
        let alpha = s.baseAlpha + twinkle * 0.45;

        // Mouse proximity glow
        const dx = s.x - mouse.x;
        const dy = s.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let glowBoost = 0;
        if (dist < GLOW_RADIUS) {
          glowBoost = (1 - dist / GLOW_RADIUS);
          alpha = Math.min(1, alpha + glowBoost * 0.6);
        }

        const r = s.r + glowBoost * 1.5;

        // Outer glow (only for boosted stars)
        if (glowBoost > 0.1) {
          ctx.beginPath();
          ctx.arc(s.x, s.y, r + 4 * glowBoost, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(180, 220, 255, ${glowBoost * 0.15})`;
          ctx.fill();
        }

        // Star dot
        ctx.beginPath();
        ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220, 235, 255, ${alpha})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      cvs.parentElement?.removeEventListener('mousemove', onMove);
      cvs.parentElement?.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const themeClass = `namaa-v2-container${isLight ? ' v2-theme-light' : ''}${presentationMode ? ' presentation-active' : ''}${statsMode ? ' stats-mode-active' : ''}${filterOpen ? ' filter-open' : ''}${projectOpen ? ' project-open' : ''}`;
  const theme = isLight ? 'light' : 'dark';

  return (
    <div ref={outerRef} className={themeClass} data-testid="saudi-map-v2-container" data-theme={theme}>
      <div ref={canvasRef} className="namaa-v2-canvas" data-testid="maplibre-canvas" />
      <canvas ref={shootingRef} className="v2-shooting-stars" data-testid="shooting-stars-canvas" />

      <div className="namaa-map-logo" data-testid="namaa-logo-v2">
        <img src="/namaa-logo.png" alt="Namaa" />
      </div>

      <div className="v2-map-controls" data-testid="v2-map-controls">
        <button className="v2-map-control-btn" data-testid="v2-zoom-in-btn"
          onClick={() => mapRef.current?.zoomIn({ duration: 400, easing: CAMERA.EASE_OUT_CUBIC })}>
          <ZoomIn size={18} />
        </button>
        <button className="v2-map-control-btn" data-testid="v2-zoom-out-btn"
          onClick={() => mapRef.current?.zoomOut({ duration: 400, easing: CAMERA.EASE_OUT_CUBIC })}>
          <ZoomOut size={18} />
        </button>
        <button className="v2-map-control-btn" data-testid="v2-reset-btn"
          onClick={() => mapRef.current?.fitBounds(SAUDI.BOUNDS, {
            padding: calcFitPadding(), duration: CAMERA.FLY_DURATION,
            easing: CAMERA.EASE_OUT_CUBIC, maxZoom: ZOOM.OVERVIEW,
          })}>
          <Maximize2 size={18} />
        </button>
      </div>

      {/* Theme toggle — manual dark/light switch */}
      <button
        className="v2-theme-toggle"
        data-testid="v2-theme-toggle"
        onClick={handleThemeToggle}
        title={isLight ? 'الوضع الداكن' : 'الوضع الفاتح'}
      >
        {isLight ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      {mapReady && <RegionPolygonsLayer map={mapRef.current} containerRef={outerRef} theme={theme} statsMode={statsMode} onStatsRegionSelect={onStatsRegionSelect} />}

      {mapReady && (
        <ProjectClusterLayer
          map={mapRef.current}
          filters={filters}
          layers={layers}
          onResultCount={onResultCount}
          onProjectSelect={onProjectSelect}
          theme={theme}
          presentationMode={presentationMode}
        />
      )}

      {mapReady && children}
    </div>
  );
};

export default MapLibreEngine;
